import type {
  MarketListing,
  MarketListingConflict,
  MarketListingDedupeConfidence,
  MarketListingSource
} from "../domain/types.ts";
import { normalizeLocationText } from "../location/locationSearch.ts";
import { publicMarketIdentityNamespace } from "./publicMarketTypes.ts";
import { canonicalPropertyType, effectiveApartmentArea, normalizeSlovakLocationName } from "../domain/propertyMatching.ts";

export type PublicMarketDedupeDecision = {
  confidence: MarketListingDedupeConfidence;
  merge: boolean;
  reason: string;
};

const tokenSet = (value?: string) => new Set(
  normalizeLocationText(value ?? "")
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 3 && !["byt", "predaj", "ponuka", "izbovy"].includes(token))
);

const tokenSimilarity = (first?: string, second?: string) => {
  const left = tokenSet(first);
  const right = tokenSet(second);
  if (left.size === 0 || right.size === 0) return 0;
  const intersection = [...left].filter((token) => right.has(token)).length;
  return intersection / new Set([...left, ...right]).size;
};

const normalizedContainment = (first?: string, second?: string) => {
  const left = normalizeSlovakLocationName(first);
  const right = normalizeSlovakLocationName(second);
  return Boolean(left && right && (left === right || (Math.min(left.length, right.length) >= 4 && (left.includes(right) || right.includes(left)))));
};

const locationSimilarity = (first: MarketListing, second: MarketListing) => Math.max(
  ...[first.street, first.locality].flatMap((left) =>
    [second.street, second.locality].map((right) => normalizedContainment(left, right) ? 1 : tokenSimilarity(left, right))
  )
);

const closeNumber = (first: number | undefined, second: number | undefined, relativeTolerance: number, absoluteTolerance: number) => {
  if (!first || !second) return false;
  return Math.abs(first - second) <= absoluteTolerance
    || Math.abs(first - second) / Math.max(first, second) <= relativeTolerance;
};

const sameText = (first?: string, second?: string) =>
  Boolean(first && second && normalizeLocationText(first) === normalizeLocationText(second));

const listingSources = (listing: MarketListing): MarketListingSource[] => listing.sources?.length
  ? listing.sources
  : listing.sourceUrl
    ? [{
      providerId: listing.sourceName,
      sourceName: listing.sourceName,
      sourceUrl: listing.sourceUrl,
      externalId: listing.externalId,
      capturedAt: listing.capturedAt
    }]
    : [];

const sourceIdentity = (source: MarketListingSource) => source.externalId
  ? `${source.identityNamespace ?? publicMarketIdentityNamespace(source.providerId)}:${source.externalId}`
  : undefined;

const hasSharedTrustedIdentity = (first: MarketListing, second: MarketListing) => {
  const right = new Set(listingSources(second).map(sourceIdentity).filter((value): value is string => Boolean(value)));
  return listingSources(first).map(sourceIdentity).some((identity) => identity && right.has(identity));
};

const canonicalSourceUrl = (value?: string) => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    url.hash = "";
    url.search = "";
    return `${url.hostname.replace(/^www\./i, "").toLocaleLowerCase("sk")}${url.pathname.replace(/\/$/, "")}`;
  } catch {
    return undefined;
  }
};

const hasSharedCanonicalUrl = (first: MarketListing, second: MarketListing) => {
  const right = new Set(listingSources(second).map((source) => canonicalSourceUrl(source.sourceUrl)).filter(Boolean));
  return listingSources(first).some((source) => right.has(canonicalSourceUrl(source.sourceUrl)));
};

const sameTechnicalAttributes = (first: MarketListing, second: MarketListing) => [
  [first.condition, second.condition],
  [first.electricity, second.electricity],
  [first.coreType, second.coreType],
  [first.kitchen, second.kitchen],
  [first.reconstruction, second.reconstruction],
  [first.buildingType, second.buildingType],
  [first.elevator, second.elevator],
  [first.balconyType, second.balconyType],
  [first.cellar, second.cellar]
].filter(([left, right]) => sameText(left, right)).length;

export const classifyPublicMarketDuplicate = (first: MarketListing, second: MarketListing): PublicMarketDedupeDecision => {
  if (hasSharedTrustedIdentity(first, second) || hasSharedCanonicalUrl(first, second)) {
    return { confidence: "EXACT_ID", merge: true, reason: "Zhodná externá identita alebo kanonická URL ponuky." };
  }
  const firstMunicipalityId = first.location?.municipalityId || first.municipalityId;
  const secondMunicipalityId = second.location?.municipalityId || second.municipalityId;
  const sameMunicipality = firstMunicipalityId && secondMunicipalityId
    ? firstMunicipalityId === secondMunicipalityId
    : normalizeSlovakLocationName(first.location?.municipality || first.city) === normalizeSlovakLocationName(second.location?.municipality || second.city);
  if (!sameMunicipality) return { confidence: "DISTINCT", merge: false, reason: "Odlišná obec." };
  if (canonicalPropertyType(first.propertyType) !== canonicalPropertyType(second.propertyType)) return { confidence: "DISTINCT", merge: false, reason: "Odlišný typ nehnuteľnosti." };
  if (first.rooms !== second.rooms) return { confidence: "DISTINCT", merge: false, reason: "Odlišný počet izieb." };
  if (!closeNumber(effectiveApartmentArea(first).value, effectiveApartmentArea(second).value, 0.03, 2)) return { confidence: "DISTINCT", merge: false, reason: "Odlišná výmera." };
  if (!closeNumber(first.askingPrice, second.askingPrice, 0.02, 1500)) return { confidence: "DISTINCT", merge: false, reason: "Odlišná ponuková cena." };
  if (first.floor !== undefined && second.floor !== undefined && first.floor !== second.floor) {
    return { confidence: "DISTINCT", merge: false, reason: "Odlišné poschodie." };
  }

  const titleSimilarity = tokenSimilarity(first.title, second.title);
  const descriptionSimilarity = tokenSimilarity(first.description, second.description);
  const sameAgency = sameText(first.agency, second.agency);
  const sameStreet = sameText(first.street, second.street);
  const localityEvidence = locationSimilarity(first, second);
  const sameFloor = first.floor !== undefined && second.floor !== undefined && first.floor === second.floor;
  const technicalMatches = sameTechnicalAttributes(first, second);
  const nearExactArea = closeNumber(effectiveApartmentArea(first).value, effectiveApartmentArea(second).value, 0.01, 0.5);
  const nearExactPrice = closeNumber(first.askingPrice, second.askingPrice, 0.005, 100);
  const strongStreetEvidence = sameStreet && (
    (sameAgency && (titleSimilarity >= 0.45 || descriptionSimilarity >= 0.45 || technicalMatches >= 2))
    || (titleSimilarity >= 0.65 && descriptionSimilarity >= 0.5)
  );
  const strongNoStreetEvidence = !sameStreet
    && sameAgency
    && sameFloor
    && titleSimilarity >= 0.65
    && descriptionSimilarity >= 0.65
    && technicalMatches >= 2;
  const strongCombinedEvidence = sameAgency
    && localityEvidence >= 0.65
    && nearExactArea
    && nearExactPrice
    && (technicalMatches >= 1 || titleSimilarity >= 0.45 || descriptionSimilarity >= 0.45);

  if (strongStreetEvidence || strongNoStreetEvidence || strongCombinedEvidence) {
    return {
      confidence: "HIGH_CONFIDENCE",
      merge: true,
      reason: strongStreetEvidence
        ? "Zhodná lokalita a silná zhoda agentúry alebo obsahu."
        : strongNoStreetEvidence
          ? "Silná zhoda agentúry, poschodia, obsahu a technických atribútov."
          : "Silná kombinovaná zhoda agentúry, lokality, výmery, ceny a technických údajov."
    };
  }
  if (sameStreet || localityEvidence >= 0.45 || sameAgency || titleSimilarity >= 0.5 || descriptionSimilarity >= 0.5 || (sameFloor && technicalMatches >= 2)) {
    return { confidence: "POSSIBLE_DUPLICATE", merge: false, reason: "Čiastočná zhoda bez dostatočných dôkazov na bezpečné zlúčenie." };
  }
  return { confidence: "DISTINCT", merge: false, reason: "Cena a výmera bez ďalších silných signálov nestačia na zlúčenie." };
};

const completeness = (listing: MarketListing) => [
  listing.title,
  listing.street,
  listing.locality,
  listing.area,
  listing.askingPrice,
  listing.condition,
  listing.electricity,
  listing.coreType,
  listing.kitchen,
  listing.floor,
  listing.totalFloors,
  listing.elevator,
  listing.balconyType,
  listing.cellar,
  listing.orientation,
  listing.buildingType,
  listing.reconstruction,
  listing.description,
  listing.agency
].filter((value) => value !== undefined && value !== "").length;

const stableHash = (value: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) hash = Math.imul(hash ^ value.charCodeAt(index), 0x01000193);
  return (hash >>> 0).toString(16).padStart(8, "0");
};

const sourceKey = (source: MarketListingSource) =>
  `${source.providerId}:${source.externalId || source.sourceUrl}`;

const hasValue = (value: unknown) => value !== undefined && value !== "";

const firstValue = <Key extends keyof MarketListing>(ranked: MarketListing[], key: Key): MarketListing[Key] | undefined =>
  ranked.find((listing) => hasValue(listing[key]))?.[key];

const conflictSource = (listing: MarketListing) => listingSources(listing)[0];

const conflictFor = (
  ranked: MarketListing[],
  field: keyof MarketListing,
  materiallyDifferent: (first: unknown, second: unknown) => boolean
): MarketListingConflict | undefined => {
  const withValues = ranked.filter((listing) => hasValue(listing[field]));
  if (withValues.length < 2 || !withValues.some((listing) => materiallyDifferent(withValues[0][field], listing[field]))) return undefined;
  return {
    field: String(field),
    values: withValues.flatMap((listing) => {
      const source = conflictSource(listing);
      const value = listing[field];
      return source && (typeof value === "string" || typeof value === "number")
        ? [{ providerId: source.providerId, sourceUrl: source.sourceUrl, value }]
        : [];
    })
  };
};

const textDiffers = (first: unknown, second: unknown) =>
  typeof first === "string" && typeof second === "string" && !sameText(first, second);
const numberDiffers = (relativeTolerance: number, absoluteTolerance: number) => (first: unknown, second: unknown) =>
  typeof first === "number" && typeof second === "number" && !closeNumber(first, second, relativeTolerance, absoluteTolerance);
const integerDiffers = (first: unknown, second: unknown) =>
  typeof first === "number" && typeof second === "number" && first !== second;

const mergeCluster = (cluster: MarketListing[], allListings: MarketListing[]): MarketListing => {
  const ranked = [...cluster].sort((first, second) =>
    completeness(second) - completeness(first)
    || Date.parse(second.capturedAt) - Date.parse(first.capturedAt)
    || first.id.localeCompare(second.id)
  );
  const primary = ranked[0];
  const sources = [...new Map(
    ranked.flatMap(listingSources).map((source) => [sourceKey(source), {
      ...source,
      identityNamespace: source.identityNamespace ?? publicMarketIdentityNamespace(source.providerId)
    }])
  ).values()].sort((first, second) => sourceKey(first).localeCompare(sourceKey(second)));
  const canonicalId = `market:${stableHash(sources.map(sourceKey).join("|"))}`;
  const capturedDates = ranked.map((listing) => listing.capturedAt).sort();
  const pairDecisions = ranked.flatMap((listing, index) => ranked.slice(index + 1).map((other) => classifyPublicMarketDuplicate(listing, other)));
  const strongest = pairDecisions.find((decision) => decision.confidence === "EXACT_ID")
    ?? pairDecisions.find((decision) => decision.confidence === "HIGH_CONFIDENCE");
  const possible = cluster.length === 1
    ? allListings
      .filter((listing) => listing.id !== primary.id)
      .map((listing) => classifyPublicMarketDuplicate(primary, listing))
      .find((decision) => decision.confidence === "POSSIBLE_DUPLICATE")
    : undefined;
  const conflicts = [
    conflictFor(ranked, "area", numberDiffers(0.03, 2)),
    conflictFor(ranked, "askingPrice", numberDiffers(0.02, 1500)),
    conflictFor(ranked, "rooms", integerDiffers),
    conflictFor(ranked, "floor", integerDiffers),
    conflictFor(ranked, "totalFloors", integerDiffers),
    conflictFor(ranked, "city", textDiffers),
    conflictFor(ranked, "street", textDiffers),
    conflictFor(ranked, "locality", textDiffers),
    conflictFor(ranked, "condition", textDiffers),
    conflictFor(ranked, "electricity", textDiffers),
    conflictFor(ranked, "coreType", textDiffers),
    conflictFor(ranked, "kitchen", textDiffers),
    conflictFor(ranked, "reconstruction", textDiffers),
    conflictFor(ranked, "buildingType", textDiffers)
  ].filter((conflict): conflict is MarketListingConflict => Boolean(conflict));
  const askingPrice = firstValue(ranked, "askingPrice") as number | undefined;
  const area = firstValue(ranked, "area") as number | undefined;
  const areas = firstValue(ranked, "areas") as MarketListing["areas"];

  return {
    ...primary,
    title: firstValue(ranked, "title") as string | undefined,
    city: firstValue(ranked, "city") as string | undefined,
    street: firstValue(ranked, "street") as string | undefined,
    locality: firstValue(ranked, "locality") as string | undefined,
    propertyType: firstValue(ranked, "propertyType") as string | undefined,
    rooms: firstValue(ranked, "rooms") as number | undefined,
    area,
    askingPrice,
    condition: firstValue(ranked, "condition") as string | undefined,
    electricity: firstValue(ranked, "electricity") as string | undefined,
    coreType: firstValue(ranked, "coreType") as string | undefined,
    kitchen: firstValue(ranked, "kitchen") as string | undefined,
    floor: firstValue(ranked, "floor") as number | undefined,
    totalFloors: firstValue(ranked, "totalFloors") as number | undefined,
    elevator: firstValue(ranked, "elevator") as string | undefined,
    balconyType: firstValue(ranked, "balconyType") as string | undefined,
    cellar: firstValue(ranked, "cellar") as string | undefined,
    orientation: firstValue(ranked, "orientation") as string | undefined,
    buildingType: firstValue(ranked, "buildingType") as string | undefined,
    reconstruction: firstValue(ranked, "reconstruction") as string | undefined,
    description: firstValue(ranked, "description") as string | undefined,
    agency: firstValue(ranked, "agency") as string | undefined,
    municipalityId: firstValue(ranked, "municipalityId") as string | undefined,
    location: firstValue(ranked, "location") as MarketListing["location"],
    areas,
    technical: firstValue(ranked, "technical") as MarketListing["technical"],
    equipment: firstValue(ranked, "equipment") as MarketListing["equipment"],
    canonicalPropertyType: firstValue(ranked, "canonicalPropertyType") as MarketListing["canonicalPropertyType"],
    fieldProvenance: firstValue(ranked, "fieldProvenance") as MarketListing["fieldProvenance"],
    pricePerM2: askingPrice ? askingPrice / (effectiveApartmentArea({ area, areas }).value ?? area ?? 1) : undefined,
    id: canonicalId,
    propertyId: canonicalId,
    sources,
    sourceUrls: [...new Set(sources.map((source) => source.sourceUrl))],
    externalIds: [...new Set(sources.flatMap((source) => source.externalId ? [source.externalId] : []))],
    sourceName: [...new Set(sources.map((source) => source.sourceName))].join(" + "),
    sourceUrl: sources[0]?.sourceUrl ?? primary.sourceUrl,
    capturedAt: capturedDates[capturedDates.length - 1] ?? primary.capturedAt,
    dedupeReason: strongest?.reason ?? possible?.reason ?? "Samostatná kanonická ponuka.",
    dedupeConfidence: strongest?.confidence ?? possible?.confidence ?? "DISTINCT",
    dedupeConflicts: conflicts.length > 0 ? conflicts : undefined
  };
};

export const dedupePublicMarketListings = (listings: MarketListing[]) => {
  let clusters: MarketListing[][] = [];
  [...listings].sort((first, second) => first.id.localeCompare(second.id)).forEach((listing) => {
    const matching = clusters.filter((items) => items.some((item) => classifyPublicMarketDuplicate(item, listing).merge));
    if (matching.length === 0) {
      clusters.push([listing]);
      return;
    }
    const matchingSet = new Set(matching);
    clusters = [
      ...clusters.filter((items) => !matchingSet.has(items)),
      [...matching.flat(), listing]
    ];
  });
  return clusters.map((cluster) => mergeCluster(cluster, listings)).sort((first, second) => first.id.localeCompare(second.id));
};
